import { ORDER_CONTEXT } from "@payment-client/constants/Order";
import { CREDIT_TYPE } from "@payment-client/interfaces/CardApi";
import {
    GetEditOrderPaymentMethodsParams,
    GetPaymentMethodsParams,
    GetPaymentOptionsParams,
    OrderContextParams,
    SuperContextParams,
    SwiggyOneContextParams,
} from "@payment-client/interfaces/PaymentApiPublic";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_ID } from "@swiggy-private/payment-types";
import { isScenesFullPaymentFlow } from "./Misc";

export type Headers = { [header: string]: string | string[] | undefined };

export const getEditOrderExtraParams = (headers: Headers): OrderContextParams => ({
    context: ORDER_CONTEXT.EDIT,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_key: (headers["cartkey"] as string) || "",
});

export const getPaymentOptionsParams = (headers: Headers): GetPaymentOptionsParams => ({
    paymentLinkId: (headers["cartkey"] as string) || "",
});

export const getDineoutPaymentOptionsParams = (headers: Headers): GetPaymentOptionsParams => {
    // For scenes full payment flow, always use SKIP_PAYMENT_LINK even if the session storage flag was cleared
    // This prevents duplicate GPO calls when cartKey appears after the first GPO call
    // Read directly from session storage instead of passing through extraParams

    const _isScenesFullPaymentFlow = isScenesFullPaymentFlow();

    //TODO: to remove this temporary logic of sending dummy payment link once BE confirms , refer slack thread: https://swiggy.slack.com/archives/C09QS6AHEP6/p1765550465157289
    const paymentLinkId = _isScenesFullPaymentFlow ? "dineout-payment-link" : (headers["cartkey"] as string) || "";

    const params: GetPaymentOptionsParams = {
        paymentLinkId,
        // Include isScenesFullPaymentFlow in params to send it as a query parameter to the GPO API
        ...(_isScenesFullPaymentFlow && { isScenesFullPaymentFlow: true }),
    };

    return params;
};

export const getPayOnDeliveryPaymentOptionsParams = (headers: Headers): GetPaymentOptionsParams => ({
    context: ORDER_CONTEXT.PAY_ON_DELIVERY,
    paymentLinkId: (headers["cartkey"] as string) || "",
});

export const getSwiggySuperParams = (headers: Headers): SuperContextParams => ({
    context: ORDER_CONTEXT.SWIGGY_SUPER,
    planId: Number(headers["planid"]) || 0,
    quantity: Number(headers["quantity"]) || 0,
});

export const getSwiggyOneParams = (): SwiggyOneContextParams => ({
    context: ORDER_CONTEXT.SWIGGY_ONE,
});

export const getRegularHeaders = (headers: Headers): Headers => {
    headers["m_id"] = headers["m_id"] ?? MARKETPLACE_ID.CHECKOUT;
    return headers;
};

export const getPaymentMethodsParamsByBusinessLine = (
    businessLine: string,
    params: GetPaymentMethodsParams,
    headers: Headers,
): [GetPaymentMethodsParams, Headers] => {
    switch (businessLine?.toLowerCase()) {
        case MARKETPLACE_BUSINESS_LINE.EDIT_ORDER: {
            const editOrderParams: GetEditOrderPaymentMethodsParams = {
                ...params,
                ...getEditOrderExtraParams(headers),
            };
            return [editOrderParams, headers];
        }
        case MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP: {
            return [
                {
                    ...params,
                    ...getPaymentOptionsParams(headers),
                },
                headers,
            ];
        }
        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY: {
            return [
                {
                    ...params,
                    ...getPayOnDeliveryPaymentOptionsParams(headers),
                },
                headers,
            ];
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER: {
            return [{ ...params, ...getSwiggySuperParams(headers) }, headers];
        }
        case MARKETPLACE_BUSINESS_LINE.DINEOUT:
            return [
                {
                    ...params,
                    ...getDineoutPaymentOptionsParams(headers),
                },
                headers,
            ];
        case MARKETPLACE_BUSINESS_LINE.DASH:
        case MARKETPLACE_BUSINESS_LINE.HANDPICKED:
        case MARKETPLACE_BUSINESS_LINE.ASSURE:
        case MARKETPLACE_BUSINESS_LINE.JUST: {
            return [
                {
                    ...params,
                    ...getPaymentOptionsParams(headers),
                },
                headers,
            ];
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY: {
            headers.m_id = headers.m_id ?? MARKETPLACE_ID.SWIGGY_MONEY;
            params.context = params.is_gifting_flow === true ? CREDIT_TYPE.VOUCHER : CREDIT_TYPE.SELFLOAD;
            // BE needs paymentAmount in the GPO request body too, not just the paymentamount header
            if (headers["paymentamount"]) {
                params.paymentAmount = String(headers["paymentamount"]);
            }
            return [params, headers];
        }
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE: {
            return [{ ...params, ...getSwiggyOneParams() }, headers];
        }
        case MARKETPLACE_BUSINESS_LINE.CBCC: {
            headers.m_id = headers.m_id ?? MARKETPLACE_ID.CBCC;
            return [params, headers];
        }
        case MARKETPLACE_BUSINESS_LINE.GIFTING: {
            headers.m_id = MARKETPLACE_ID.GIFTING;
            return [params, headers];
        }
        case MARKETPLACE_BUSINESS_LINE.REGULAR: {
            headers = getRegularHeaders(headers);
            return [params, headers];
        }
        default: {
            return [params, headers];
        }
    }
};
